/**
 * The status tags of an Events365 ticket — the "ticket-tags" row of the B2C
 * TicketCard. Shared so the B2C cart, the agent "copy quote" text and the
 * backoffice order summary list the same tags as the card does.
 *
 * Labels are the caller's job (each app has its own translations); this only
 * decides which tags apply and carries the provider texts that go with them.
 * The secondary category name is not a tag here: callers show it next to the
 * ticket name.
 */

export type EventTicketTagType =
  | 'instantConfirmation'
  | 'offlineStock'
  | 'seating'
  | 'hold24Hours'
  | 'vipPackage';

export interface EventTicketTag {
  type: EventTicketTagType;
  /** Provider text shown as the label itself (seating arrangement). */
  text?: string;
  /** Provider text for a tooltip (instant-confirmation text, 24h-hold message). */
  hint?: string;
  /** Seating only: every seat together (shown green) vs split (orange). */
  allTogether?: boolean;
}

/**
 * Ticket fields sit under `ticketDetails` on cart-built order products; some
 * saved orders carry them under `ticket` (the booking service reads both).
 */
export const getEventTicketDetails = (productData: any): any =>
  productData?.ticketDetails ?? productData?.ticket;

export const getEventTicketTags = (ticket: any): EventTicketTag[] => {
  if (!ticket) {
    return [];
  }

  const tags: EventTicketTag[] = [];

  if (ticket.immediateConfirmation) {
    tags.push({
      type: 'instantConfirmation',
      hint: ticket.immediateConfirmationText || undefined,
    });
  }
  // No provider hint: Events365 sometimes sends the online-stock text
  // (guaranteed confirmation) on offline-stock tickets, so callers show their
  // own static explanation instead.
  if (ticket.immediateConfirmation === false) {
    tags.push({ type: 'offlineStock' });
  }

  const sittingArrangements =
    typeof ticket.sittingArrangements === 'string'
      ? ticket.sittingArrangements.trim()
      : '';
  if (sittingArrangements) {
    tags.push({
      type: 'seating',
      text: sittingArrangements,
      allTogether: sittingArrangements.toLowerCase().includes('all together'),
    });
  }

  if (ticket.onHold24Hours?.status) {
    tags.push({
      type: 'hold24Hours',
      hint: ticket.onHold24Hours.publicMessage || undefined,
    });
  }

  if (ticket.isVipPackage) {
    tags.push({ type: 'vipPackage' });
  }

  return tags;
};
